import React, { useState, useMemo } from 'react';
import { PathNode } from '../utils/jsonPathUtils';
import { getMatchingNodePaths, highlightMatches } from '../utils/searchUtils';
import { usePlatform } from '../hooks/usePlatform';
import { copyToClipboard } from '../utils/clipboardUtils';
import { getInteractionClasses } from '../utils/platformUtils';

interface PathDisplayProps {
  paths: PathNode[];
  onPathSelect: (path: string) => void;
  selectedPath: string | null;
  searchQuery?: string;
  isRegexSearch?: boolean;
}

interface TouchState {
  startTime: number;
  startX: number;
  startY: number;
  isLongPress: boolean;
}

interface PathNodeComponentProps {
  node: PathNode;
  onPathSelect: (path: string) => void;
  selectedPath: string | null;
  level: number;
  searchQuery?: string;
  isRegexSearch?: boolean;
  matchingPaths?: Set<string>;
}

const PathNodeComponent: React.FC<PathNodeComponentProps> = ({ 
  node, 
  onPathSelect, 
  selectedPath, 
  level,
  searchQuery = '',
  isRegexSearch = false,
  matchingPaths = new Set()
}) => {
  const platform = usePlatform();
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedPath === node.path;
  const isMatching = matchingPaths.has(node.path);
  
  // Auto-expand nodes that have matching children or are matching themselves
  const shouldAutoExpand = searchQuery && (isMatching || 
    (node.children && node.children.some(child => matchingPaths.has(child.path))));
  const [isExpanded, setIsExpanded] = useState(level < 2 || shouldAutoExpand);
  const [touchState, setTouchState] = useState<TouchState | null>(null);
  const [showCopyFeedback, setShowCopyFeedback] = useState(false);

  const handleToggle = () => {
    if (hasChildren) {
      setIsExpanded(!isExpanded);
    }
  };

  const handlePathClick = () => {
    onPathSelect(node.path);
  };

  // Touch handlers for mobile long-press copy functionality
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!platform.isMobile) return;
    
    const touch = e.touches[0];
    setTouchState({
      startTime: Date.now(),
      startX: touch.clientX,
      startY: touch.clientY,
      isLongPress: false
    });

    // Set up long press timer
    setTimeout(() => {
      if (touchState && !touchState.isLongPress) {
        setTouchState(prev => prev ? { ...prev, isLongPress: true } : null);
        handleLongPress();
      }
    }, 500); // 500ms for long press
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!platform.isMobile || !touchState) return;
    
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchState.startX);
    const deltaY = Math.abs(touch.clientY - touchState.startY);
    
    // If finger moved too much, cancel long press
    if (deltaX > 10 || deltaY > 10) {
      setTouchState(null);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!platform.isMobile || !touchState) return;
    
    const touchDuration = Date.now() - touchState.startTime;
    
    if (!touchState.isLongPress && touchDuration < 500) {
      // Short tap - normal click behavior
      handlePathClick();
    }
    
    setTouchState(null);
  };

  const handleLongPress = async () => {
    if (!platform.isMobile) return;
    
    // Provide haptic feedback if available
    if ('vibrate' in navigator) {
      navigator.vibrate(50);
    }

    // Copy path to clipboard
    const fullPath = node.path === '' ? '$' : 
                    node.path.startsWith('[') ? `$${node.path}` : 
                    `$.${node.path}`;
    
    const result = await copyToClipboard(fullPath, platform);
    
    if (result.success) {
      setShowCopyFeedback(true);
      setTimeout(() => setShowCopyFeedback(false), 2000);
    }
  };

  const getValuePreview = (value: any): string => {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (typeof value === 'string') return `"${value.length > 20 ? value.substring(0, 20) + '...' : value}"`;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) return `Array(${value.length})`;
    if (typeof value === 'object') return `Object(${Object.keys(value).length})`;
    return String(value);
  };

  const renderHighlightedText = (text: string) => {
    if (!searchQuery || !isMatching) return text;
    const highlighted = highlightMatches(text, searchQuery, isRegexSearch);
    return <span dangerouslySetInnerHTML={{ __html: highlighted }} />;
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'object': return '{}';
      case 'array': return '[]';
      default: return '';
    }
  };

  return (
    <div className="select-none">
      <div 
        className={`relative flex items-center py-2 px-2 sm:px-3 rounded-xl cursor-pointer border border-transparent transition-all duration-150 ${getInteractionClasses(platform)} ${
          isSelected ? 'bg-primary-container/10 border-primary-container/40 shadow-[0_0_18px_rgba(0,242,255,0.12)]' : 'hover:bg-white/[0.035] hover:border-white/10'
        } ${
          isMatching ? 'bg-tertiary-container/10 border-tertiary-container/35' : ''
        } ${
          showCopyFeedback ? 'bg-primary-container/15 border-primary-container/45' : ''
        }`}
        style={{ 
          paddingLeft: `${level * (platform.isMobile ? 12 : 20) + (platform.isMobile ? 8 : 12)}px`,
          minHeight: platform.isMobile ? '44px' : '32px'
        }}
        onClick={platform.isMobile ? undefined : handlePathClick}
        onTouchStart={platform.isMobile ? handleTouchStart : undefined}
        onTouchMove={platform.isMobile ? handleTouchMove : undefined}
        onTouchEnd={platform.isMobile ? handleTouchEnd : undefined}
      >
        {showCopyFeedback && (
          <div className="absolute top-0 right-0 bg-primary-container text-on-primary text-xs px-2 py-1 rounded-bl-lg rounded-tr-lg">
            Copied!
          </div>
        )}
        {hasChildren && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleToggle();
            }}
            className={`mr-2 sm:mr-3 flex items-center justify-center rounded-md text-outline hover:text-primary-container ${getInteractionClasses(platform)}`}
            style={{
              width: platform.isMobile ? '32px' : '16px',
              height: platform.isMobile ? '32px' : '16px',
              minHeight: platform.isMobile ? '32px' : '16px'
            }}
          >
            {isExpanded ? '▼' : '▶'}
          </button>
        )}
        
        {!hasChildren && <div className="w-5 sm:w-7" />}
        
        <div className="flex flex-col sm:flex-row sm:items-center flex-1 min-w-0 gap-1 sm:gap-0">
          <div className="flex items-center min-w-0 flex-1">
            <span className="font-code text-xs sm:text-sm text-primary-container font-medium mr-2 sm:mr-3 truncate">
              {renderHighlightedText(node.path || 'root')}
            </span>
            
            {node.type !== 'primitive' && (
              <span className="text-xs text-secondary mr-2 sm:mr-3 font-code font-medium flex-shrink-0">
                {getTypeIcon(node.type)}
              </span>
            )}
          </div>
          
          <span className="text-xs text-on-surface-variant truncate font-body pl-6 sm:pl-0">
            {renderHighlightedText(getValuePreview(node.value))}
          </span>
        </div>
      </div>
      
      {hasChildren && isExpanded && (
        <div>
          {node.children!.map((child, index) => (
            <PathNodeComponent
              key={`${child.path}-${index}`}
              node={child}
              onPathSelect={onPathSelect}
              selectedPath={selectedPath}
              level={level + 1}
              searchQuery={searchQuery}
              isRegexSearch={isRegexSearch}
              matchingPaths={matchingPaths}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const PathDisplay: React.FC<PathDisplayProps> = ({ 
  paths, 
  onPathSelect, 
  selectedPath, 
  searchQuery = '', 
  isRegexSearch = false 
}) => {
  const platform = usePlatform();
  const matchingPaths = useMemo(() => {
    if (!searchQuery) return new Set<string>();
    return getMatchingNodePaths(paths, searchQuery, isRegexSearch);
  }, [paths, searchQuery, isRegexSearch]);

  return (
    <div className="code-surface scrollbar-thin flex-1 overflow-auto border border-white/10 rounded-xl shadow-[inset_0_0_28px_rgba(0,0,0,0.35)]">
      {platform.isMobile && paths.length > 0 && (
        <div className="p-2 bg-primary-container/10 border-b border-primary-container/20">
          <p className="text-xs text-primary-container text-center">
            💡 <strong>Tip:</strong> Long press on paths to copy them to clipboard
          </p>
        </div>
      )}
      {paths.length === 0 ? (
        <div className="relative flex min-h-[360px] items-center justify-center p-4 text-center sm:p-8">
          <div className="scanline absolute left-0 top-10 h-px w-full"></div>
          <div>
          <div className="text-primary-container/70 mb-2">
            <svg className="w-14 sm:w-20 h-14 sm:h-20 mx-auto mb-4 opacity-70 rounded-2xl border border-primary-container/25 bg-primary-container/10 p-4 shadow-[0_0_36px_rgba(0,242,255,0.10)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p className="text-lg sm:text-xl text-on-surface font-display font-semibold">No JSON data to display</p>
          <p className="text-on-surface-variant text-xs sm:text-sm mt-2 font-body">Paste valid JSON in the left panel to see paths</p>
          </div>
        </div>
      ) : (
        <div className="p-3">
          {searchQuery && matchingPaths.size === 0 ? (
            <div className="p-4 sm:p-8 text-center">
              <div className="text-outline mb-2">
                <svg className="w-8 sm:w-12 h-8 sm:h-12 mx-auto mb-4 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <p className="text-sm sm:text-base text-on-surface font-display font-medium">No matches found</p>
              <p className="text-on-surface-variant text-xs sm:text-sm mt-2 font-body">
                Try adjusting your search query or check the regex syntax
              </p>
            </div>
          ) : (
            paths.map((path, index) => (
              <PathNodeComponent
                key={`${path.path}-${index}`}
                node={path}
                onPathSelect={onPathSelect}
                selectedPath={selectedPath}
                level={0}
                searchQuery={searchQuery}
                isRegexSearch={isRegexSearch}
                matchingPaths={matchingPaths}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default PathDisplay;